package pl.decks.personal.pages.ievp.sections.profile

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.lightened
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.border
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import pl.decks.chartjs.legend.LegendAlign
import pl.decks.chartjs.legend.LegendPosition
import pl.decks.chartjs.mode.InteractionMode
import pl.decks.chartjs.radar.RadarConfig
import pl.decks.chartjs.radar.builder.RadarConfigBuilder
import pl.decks.chartjs.silk.color.toChartColor
import pl.decks.chartjs.silk.radar.SilkyRadarChart
import pl.decks.chartjs.style.ChartLabel
import pl.decks.chartjs.value.FillValue
import pl.decks.personal.components.widgets.disclaimer.DisclaimerBox
import pl.decks.personal.extensions.toSitePalette
import pl.decks.personal.pages.ievp.IEVPJourney
import pl.decks.personal.pages.ievp.IEVPSection
import pl.decks.personal.styles.FontTypography
import pl.decks.personal.styles.SiteLayout
import pl.decks.posthog.client.PostHogClient
import kotlin.math.pow

@OptIn(DelicateApi::class)
@Composable
fun ProfileSectionContent(headingProgress: Float) {
    val breakpoint = rememberBreakpoint()
    val colorMode = ColorMode.current
    val radarDims = if (breakpoint >= Breakpoint.MD) ProfileRadarDimensions.Full else ProfileRadarDimensions.Small
    val config = remember(colorMode, radarDims) { profileRadarConfig(colorMode, radarDims) }
    val contentOpacity = headingProgress.pow(6).times(100).toInt().percent
    Column(
        modifier = Modifier.fillMaxWidth().opacity(contentOpacity),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        DisclaimerBox(
            modifier = Modifier
                .fillMaxWidth()
                .fontSize(FontTypography.Size.Small)
                .margin(topBottom = SiteLayout.Space.Medium)
                .onClick { PostHogClient.capture(IEVPJourney.DisclaimerOpened(IEVPSection.PROFILE)) }
        ) {
            SpanText("How I've split the dimensions is definitely arbitrary. " +
                    "I obviously present it favourably, yet I want to be honest about the shortcomings.")
        }
        Box(
            modifier = ProfileSectionStylesheet.RadarContainer.toModifier(),
            contentAlignment = Alignment.Center,
        ) {
            if (headingProgress > 0.4f) SilkyRadarChart("profile-radar", config, modifier = Modifier.fillMaxWidth())
        }
        ProfileLegend(
            Modifier
                .setVariable(ProfileSectionStylesheet.Vars.iconSize, radarDims.pointLabelSize.px)
                .setVariable(ProfileSectionStylesheet.Vars.textSize, radarDims.dimensionLabelSize.px)
                .setVariable(ProfileSectionStylesheet.Vars.textGap, radarDims.pointLabelPadding.px)
                .setVariable(ProfileSectionStylesheet.Vars.listingGap, (radarDims.pointLabelPadding / 2).px)
        )
    }
}

@Composable
fun ProfileLegend(modifier: Modifier = Modifier) =
    Column(ProfileSectionStylesheet.Legend.Listing.toModifier().then(modifier)) {
        val sitePalette = ColorMode.current.toSitePalette()
        ProfileDimension.entries.forEach { section ->
            ProfileLegendRow(
                section,
                modifier = Modifier.setVariable(
                    ProfileSectionStylesheet.Vars.dimensionColor,
                    section.color(sitePalette)
                )
            )
        }
    }


@Composable
private fun ProfileLegendRow(
    section: ProfileDimension,
    modifier: Modifier = Modifier,
) = Row(
    ProfileSectionStylesheet.Legend.Entry.toModifier().then(modifier),
    verticalAlignment = Alignment.CenterVertically,
) {
    section.icon(ProfileSectionStylesheet.Legend.Icon.toModifier())
    Box(ProfileSectionStylesheet.Legend.DotsContainer.toModifier()) {
        SpanText(".".repeat(200), ProfileSectionStylesheet.Legend.Dots.toModifier())
    }
    SpanText(section.title, ProfileSectionStylesheet.Legend.Title.toModifier())
}

private data class ProfileRadarDimensions(
    val pointLabelPadding: Int,
    val pointLabelSize: Int,
    val mainLineWidth: Float,
    val legendLabelPadding: Int,
    val legendLabelSize: Int,
    val dimensionLabelSize: Int,
    val pointRadius: Int,
) {
    companion object {
        val Small = ProfileRadarDimensions(
            pointLabelPadding = 10,
            pointLabelSize = 18,
            mainLineWidth = 2f,
            legendLabelPadding = 16,
            legendLabelSize = 14,
            dimensionLabelSize = 16,
            pointRadius = 4,
        )
        val Full = ProfileRadarDimensions(
            pointLabelPadding = 12,
            pointLabelSize = 24,
            mainLineWidth = 3f,
            legendLabelPadding = 20,
            legendLabelSize = 16,
            dimensionLabelSize = 18,
            pointRadius = 6,
        )
    }
}

private fun profileRadarConfig(
    colorMode: ColorMode,
    dim: ProfileRadarDimensions,
): RadarConfig {
    val palette = colorMode.toPalette()
    val sitePalette = colorMode.toSitePalette()

    val previousColor = sitePalette.accents.secondary.toRgb()
    val currentColor = sitePalette.accents.primary.toRgb()
    val backgroundColor = sitePalette.surface.toRgb()

    val labels = ProfileDimension.entries.map { ChartLabel(it.faGlyph) }
    val labelsTitles = ProfileDimension.entries.map { it.title }
    val labelsChartColors = ProfileDimension.entries.map { it.color(sitePalette).toChartColor() }

    return RadarConfig(labels) {
        interaction { mode = InteractionMode.Index }
        scale {
            chartBackgroundColor = backgroundColor.toChartColor()
            min = 0f
            max = 100f
            ticks {
                display = false
                stepSize = 10f
            }
            pointLabels {
                padding = dim.pointLabelPadding
                color(labelsChartColors)
                font {
                    size = dim.pointLabelSize
                    family = FontTypography.Family.FONT_AWESOME
                    weight(900)
                }
            }
            grid {
                circular = true
                val noTicks = 11
                lineWidth(List(noTicks) { if (it == noTicks - 1) dim.mainLineWidth else 1f })
                color(List(noTicks) {
                    if (it == noTicks - 1) palette.color.toChartColor()
                    else palette.border.toChartColor()
                })
            }
            angleLines {
                lineWidth(dim.mainLineWidth)
                color(labelsChartColors)
            }
        }
        legend {
            display = true
            position = LegendPosition.Bottom
            align = LegendAlign.Center
            labels {
                padding = dim.legendLabelPadding
                font {
                    size = dim.legendLabelSize
                    family = FontTypography.Family.MERRIWEATHER_SANS
                    weight(500)
                }
            }
        }
        tooltip {
            tooltipBackgroundColor = backgroundColor.toChartColor()
            borderColor = palette.border.toChartColor()
            titleColor = palette.color.toChartColor()
            bodyColor = palette.color.toChartColor()
            borderWidth = dim.mainLineWidth.toInt()
            callbacks {
                title { items ->
                    val index = items[0].dataIndex as Int
                    labelsTitles[index]
                }
            }
        }
        addProfileSnapshot(
            "baseline",
            data = listOf(75, 62, 79, 60, 69, 51),
            outlineColor = previousColor,
            fillColor = if (colorMode.isLight) previousColor.lightened() else previousColor.darkened(),
            pointColor = backgroundColor,
            pointRadius = dim.pointRadius,
            pointBorder = dim.mainLineWidth.toInt(),
        )
        addProfileSnapshot(
            "2.5 years growth",
            data = listOf(89, 79, 86, 66, 90, 67),
            outlineColor = currentColor,
            fillColor = currentColor.copyf(alpha = 0.5f),
            pointColor = backgroundColor,
            pointRadius = dim.pointRadius,
            pointBorder = dim.mainLineWidth.toInt(),
        )
    }
}

private fun RadarConfigBuilder.addProfileSnapshot(
    label: String,
    data: List<Number>,
    outlineColor: Color,
    fillColor: Color,
    pointColor: Color,
    pointRadius: Int,
    pointBorder: Int,
) = addDataset(label, data) {
    line {
        borderWidth(pointBorder)
        borderColor(outlineColor.toChartColor())
        fill(FillValue.Origin)
        backgroundColor(fillColor.toChartColor())
        tension = 0.25f
    }
    point {
        borderWidth(pointBorder)
        radius(pointRadius)
        borderColor(outlineColor.toChartColor())
        backgroundColor(pointColor.toChartColor())
    }
    pointHover {
        borderWidth(pointBorder)
        borderColor(outlineColor.toChartColor())
        backgroundColor(pointColor.toChartColor())
        radius(2 * pointRadius)
    }
}
